import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, resolveSupportedLanguage } from '../../i18n';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';
import { navigationLogger as logger } from '../../lib/logger';

export const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation('common');
  const { isAutoLinked, supportedCurrencies, enableAutoLink } = useCurrency();
  const { theme } = useTheme(); // Récupérer le thème actuel du contexte
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLButtonElement | null>(null);
  
  const currentLang = resolveSupportedLanguage(i18n.language) || i18n.language;
  const currentLangConfig = SUPPORTED_LANGUAGES[currentLang as keyof typeof SUPPORTED_LANGUAGES];

  // Close dropdown when clicking outside — only while open (INP)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Scroll to active language when opening
  useEffect(() => {
    if (isOpen && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: 'center' });
    }
  }, [isOpen, currentLang]);

  const handleLanguageChange = (lang: string) => {
    // Force l'enregistrement dans localStorage avant de changer la langue
    localStorage.setItem('i18nextLng', lang);
    setIsOpen(false);
    
    // Préserver la page actuelle ET le thème/univers lors du changement de langue
    const currentPath = window.location.pathname;
    const langConfig = SUPPORTED_LANGUAGES[lang as keyof typeof SUPPORTED_LANGUAGES];
    
    logger.debug('Language change:', { currentPath, lang, langConfig });
    
    // Extraire le thème/univers actuel de l'URL (nightclub, resto, office)
    const pathParts = currentPath.split('/').filter(Boolean);
    let currentTheme = pathParts.find(part => ['resto', 'office', 'nightclub'].includes(part));
    
    // Si pas de thème dans l'URL, utiliser le thème du contexte (pour préserver l'univers actuel)
    if (!currentTheme && theme) {
      currentTheme = theme;
      logger.debug('Theme not found in URL, using context:', currentTheme);
    }
    
    // Extraire la page actuelle (pricing, compare, etc.)
    const currentPage = pathParts.find(part => ['pricing', 'compare', 'contact', 'about', 'blog'].includes(part));
    
    logger.debug('Current theme:', currentTheme, 'Current page:', currentPage);
    
    // Extraire la partie de l'URL sans préfixe de langue
    // Regex améliorée pour capturer les préfixes de langue existants (ex: /fr, /en-us, /en-US, /en-za, /ar-ae)
    const langRegex = /^\/([a-z]{2}(?:-[a-zA-Z]{2})?)(\/.*)?$/i;

    // Supprime tous les préfixes langue éventuellement chaînés (ex: /en-za/ar-ae/en-sg -> /)
    let pathWithoutLang: string = currentPath;
    let pass = 0;
    while (true) {
      const m = pathWithoutLang.match(langRegex);
      if (m && m[1]) {
        pathWithoutLang = m[2] || '/';
        pass++;
      } else {
        break;
      }
    }

    // Reconstruire le chemin en préservant la page actuelle
    let preservedPath = pathWithoutLang;
    
    // Si on a un thème dans le contexte mais pas dans l'URL, l'injecter
    if (currentTheme && !pathWithoutLang.includes(currentTheme) && pathWithoutLang === '/') {
      preservedPath = `/${currentTheme}`;
    }
    
    // Construire la nouvelle URL avec le nouveau préfixe de langue
    let newPath: string;
    if (langConfig?.path) {
      newPath = `${langConfig.path}${preservedPath}`;
    } else {
      newPath = preservedPath;
    }
    
    logger.debug('New URL (theme preserved):', newPath);
    
    // Afficher un indicateur si la liaison automatique est active
    if (isAutoLinked && langConfig?.currency && supportedCurrencies[langConfig.currency]) {
      logger.debug(`Auto currency change: ${lang} → ${langConfig.currency}`);
    }
    
    // Changer la langue dans i18next SANS recharger la page
    i18n.changeLanguage(lang).then(() => {
      // Mettre à jour l'URL avec history.pushState (pas de rechargement)
      const newUrl = newPath + window.location.search + window.location.hash;
      window.history.pushState({}, '', newUrl);
      
      // Déclencher un événement popstate pour que les composants réagissent au changement d'URL
      window.dispatchEvent(new PopStateEvent('popstate'));

      // Réappliquer la devise liée à la langue (fr-CH → CHF, etc.)
      if (isAutoLinked) {
        enableAutoLink();
      }
      
      logger.debug('Language changed, URL updated without reload');
    });
  };

  // Organisation des langues par régions selon les nouvelles spécifications
  const languageRegions = {
    english: {
      titleKey: "english",
      emoji: "🇬🇧🇺🇸",
      languages: ['en-CA', 'en-US', 'en']
    },
    switzerland: {
      titleKey: "switzerland",
      emoji: "🇨🇭",
      languages: ['fr-CH', 'de-CH', 'it-CH']
    },
    europe: {
      titleKey: "europe",
      emoji: "🇪🇺",
      languages: ['de', 'da', 'es', 'fi', 'fr', 'el', 'it', 'no', 'nl', 'pt', 'sv']
    },
    slavic: {
      titleKey: "slavic",
      emoji: "🇷🇺",
      // SANCTIONS: 'ru' temporarily hidden - Russia
      languages: ['bg', 'hr', 'sk', 'sl', 'et', 'hu', 'lv', 'lt', 'pl', 'ro', 'cs', 'uk']
    },
    asia: {
      titleKey: "asia",
      emoji: "🌏",
      languages: ['zh', 'id', 'ja', 'ko', 'ms', 'en-SG', 'th', 'vi', 'hi']
    },
    middleEast: {
      titleKey: "middleEast",
      emoji: "🏛️",
      // SANCTIONS: 'fa' temporarily hidden - Iran
      languages: ['ar', 'ar-AE', 'he', 'tr']
    },
    africa: {
      titleKey: "africa",
      emoji: "🌍",
      languages: ['en-ZA', 'ar-EG', 'ar-MA']
    },
    oceania: {
      titleKey: "oceania", 
      emoji: "🌎",
      languages: ['en-AU', 'en-NZ']
    },
    americaSouth: {
      titleKey: "americaSouth",
      emoji: "🇧🇷",
      languages: ['pt-BR', 'es-MX']
    }
  };

  // Mapping des langues vers leurs codes pays pour les traductions
  const getCountryCodeForLanguage = (lang: string): string | null => {
    const mapping: Record<string, string> = {
      'en': 'GB', 'en-US': 'US', 'en-CA': 'CA', 'en-SG': 'SG', 'en-ZA': 'ZA', 'en-AU': 'AU', 'en-NZ': 'NZ',
      'fr': 'FR', 'de': 'DE', 'es': 'ES', 'es-ES': 'ES', 'es-MX': 'MX', 'it': 'IT', 'pt': 'PT', 'pt-BR': 'BR',
      'nl': 'NL', 'pl': 'PL', 'sv': 'SE', 'da': 'DK', 'no': 'NO', 'fi': 'FI', 'cs': 'CZ', 'sk': 'SK',
      'hu': 'HU', 'ro': 'RO', 'bg': 'BG', 'hr': 'HR', 'sl': 'SI', 'et': 'EE', 'lv': 'LV', 'lt': 'LT',
      'el': 'GR', 'mt': 'MT', 'tr': 'TR', 'uk': 'UA', 'zh': 'CN', 'ja': 'JP', 'ko': 'KR',
      'hi': 'IN', 'th': 'TH', 'vi': 'VN', 'id': 'ID', 'ms': 'MY', 'ar': 'SA', 'ar-AE': 'AE', 'ar-EG': 'EG',
      'ar-MA': 'MA', 'he': 'IL'
      // SANCTIONS: 'ru': 'RU' and 'fa': 'IR' temporarily hidden
    };
    return mapping[lang] || null;
  };

  const renderLanguageButton = (lang: string, config: any) => {
    const countryCode = getCountryCodeForLanguage(lang);
    const countryName = countryCode ? t(`countries.${countryCode}`) : null;
    const displayName = (
      lang === 'fr-CH' ? 'Suisse FR' :
      lang === 'de-CH' ? 'Suisse DE' :
      lang === 'it-CH' ? 'Suisse IT' : t(`languages.${lang}`)
    );
    // Remove parenthetical country/region if we already show country on the right
    const cleanedDisplayName = countryName ? displayName.replace(/\s*\([^)]*\)\s*$/, '') : displayName;
    
    return (
      <button
        key={lang}
        onClick={() => handleLanguageChange(lang)}
        ref={currentLang === lang ? activeItemRef : undefined}
        className={`w-full ${i18n.dir(i18n.language)==='rtl' ? 'text-right' : 'text-left'} px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
          currentLang === lang 
            ? 'bg-yellow-300 dark:bg-yellow-400 text-black' 
            : 'text-gray-700 dark:text-gray-300'
        }`}
      >
        <div className="flex items-center space-x-2">
          <span className="w-5">{config.flag}</span>
          <span className="flex-1">{cleanedDisplayName}</span>
          {countryName && countryName !== `countries.${countryCode}` && (
            <span className="text-xs text-gray-500">{countryName}</span>
          )}
        </div>
      </button>
    );
  };

  const renderRegionSection = (region: any, isFirst: boolean = false) => (
    <div key={region.titleKey}>
      <div className={`px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 ${isFirst ? '' : 'mt-2'}`}>
        <span className="me-1">{region.emoji}</span>
        {t(`languageRegions.${region.titleKey}`)}
      </div>
      {region.languages
        .filter((lang: string) => SUPPORTED_LANGUAGES[lang as keyof typeof SUPPORTED_LANGUAGES])
        .map((lang: string) => {
          const config = SUPPORTED_LANGUAGES[lang as keyof typeof SUPPORTED_LANGUAGES];
          return renderLanguageButton(lang, config);
        })}
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center justify-center p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        data-testid="language-selector"
        aria-label={t('navbar.selectLanguage')}
        title={t('navbar.selectLanguage')}
      >
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{currentLangConfig?.flag}</span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50">
          <div className="py-1 max-h-80 overflow-y-auto">
            {renderRegionSection(languageRegions.english, true)}
            {renderRegionSection(languageRegions.switzerland)}
            {renderRegionSection(languageRegions.europe)}
            {renderRegionSection(languageRegions.slavic)}
            {renderRegionSection(languageRegions.asia)}
            {renderRegionSection(languageRegions.middleEast)}
            {renderRegionSection(languageRegions.africa)}
            {renderRegionSection(languageRegions.oceania)}
            {renderRegionSection(languageRegions.americaSouth)}
          </div>
        </div>
      )}
    </div>
  );
};